import React from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { DISH_IMAGES } from "../data";
import Reveal from "./Reveal";
import OliveBranch from "./OliveBranch";

export const FeaturedDishes = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const items = t.dishes.items;

  return (
    <section id="dishes" className="py-24 md:py-32 px-5 md:px-10 bg-qs-bg2">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center flex flex-col items-center mb-16">
          <p className="font-outfit text-qs-red tracking-[0.3em] uppercase text-xs mb-4">
            {t.dishes.eyebrow}
          </p>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl text-qs-text">
            {t.dishes.title}
          </h2>
          <OliveBranch className="w-24 text-qs-gold my-5" />
          <p className="font-outfit text-qs-text2 max-w-2xl">{t.dishes.subtitle}</p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {items.map((dish, i) => (
            <Reveal
              key={i}
              delay={i * 90}
              className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}
            >
              <article
                data-testid={`dish-card-${i}`}
                className="group relative overflow-hidden bg-qs-bg shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer h-full"
                onClick={() => navigate("/speisekarte")}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={DISH_IMAGES[i]}
                    alt={dish.name}
                    className="w-full h-full object-cover object-[50%_62%] transition-transform duration-[900ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-70" />
                  <span className="absolute top-4 left-4 font-script text-qs-gold text-2xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-cormorant text-2xl text-qs-text mb-2 group-hover:text-qs-red transition-colors">
                    {dish.name}
                  </h3>
                  <p className="font-outfit text-sm text-qs-text2 leading-relaxed">
                    {dish.desc}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-4 text-qs-gold text-xs font-semibold tracking-widest uppercase">
                    <span className="h-px w-6 bg-qs-gold transition-all duration-300 group-hover:w-10" />
                  </span>
                </div>
              </article>
            </Reveal>
          ))}

          {/* CTA card */}
          <Reveal delay={items.length * 90} className="hidden lg:block">
            <button
              data-testid="dishes-menu-cta"
              onClick={() => navigate("/speisekarte")}
              className="group flex flex-col items-center justify-center text-center h-full w-full bg-qs-red text-white p-8 hover:bg-qs-reddark transition-colors duration-500"
            >
              <OliveBranch className="w-20 text-qs-gold mb-4" />
              <span className="font-cormorant text-2xl mb-3">{t.dishes.cta}</span>
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" />
            </button>
          </Reveal>
        </div>

        <Reveal className="flex justify-center mt-12 lg:hidden">
          <button
            data-testid="dishes-menu-cta-mobile"
            onClick={() => navigate("/speisekarte")}
            className="inline-flex items-center gap-3 border border-qs-red text-qs-red rounded-full px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase hover:bg-qs-red hover:text-white transition-colors"
          >
            {t.dishes.cta} <ArrowRight size={16} />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default FeaturedDishes;
